import { useAnalytics } from 'app/context/Analytics';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import useLinks from 'app/modules/tailor/hooks/useLinks';
import { useCallback } from 'react';

import { ContactFormLinkLocation } from '../stores/ContactFormStore';
import { useContactFormState } from '../stores/useContactFormState';

export default function useContactFormTracking() {
  const { captureEventWithActiveScreen } = useAnalytics();
  const { openedThrough } = useContactFormState();
  const { shouldShowInterstitial } = useLinks();

  const captureContactFormOpened = useCallback(
    (contactFormSlug?: string, overrideOpenedThrough?: ContactFormLinkLocation) => {
      captureEventWithActiveScreen(PosthogEventName.ContactFormOpened, {
        contactformName: contactFormSlug,
        openedThrough: overrideOpenedThrough ?? openedThrough,
      });
    },
    [captureEventWithActiveScreen, openedThrough]
  );

  const captureContactFormView = useCallback(
    (pageNumber: number, contactFormSlug?: string, overrideOpenedThrough?: ContactFormLinkLocation) => {
      captureEventWithActiveScreen(PosthogEventName.ContactFormView, {
        pageNumber,
        contactformName: contactFormSlug,
        openedThrough: overrideOpenedThrough ?? openedThrough,
      });
    },
    [captureEventWithActiveScreen, openedThrough]
  );

  const captureContactFormContinue = useCallback(
    (pageNumber: number, contactFormSlug?: string, overrideOpenedThrough?: ContactFormLinkLocation) => {
      captureEventWithActiveScreen(PosthogEventName.ContactFormContinue, {
        pageNumber,
        contactformName: contactFormSlug,
        openedThrough: overrideOpenedThrough ?? openedThrough,
      });
    },
    [captureEventWithActiveScreen, openedThrough]
  );

  const captureContactFormClosed = useCallback(
    (pageNumber: number, contactFormSlug?: string, overrideOpenedThrough?: ContactFormLinkLocation) => {
      captureEventWithActiveScreen(PosthogEventName.ContactFormClosed, {
        pageNumber,
        contactformName: contactFormSlug,
        openedThrough: overrideOpenedThrough ?? openedThrough,
      });
    },
    [captureEventWithActiveScreen, openedThrough]
  );

  const captureContactFormComplete = useCallback(
    (pageNumber: number, contactFormSlug?: string, overrideOpenedThrough?: ContactFormLinkLocation) => {
      captureEventWithActiveScreen(PosthogEventName.ContactFormComplete, {
        pageNumber,
        contactformName: contactFormSlug,
        openedThrough: overrideOpenedThrough ?? openedThrough,
      });
    },
    [captureEventWithActiveScreen, openedThrough]
  );

  const captureContactFormError = useCallback(
    (fieldName: string, message: string, contactFormSlug?: string) => {
      captureEventWithActiveScreen(PosthogEventName.ContactFormError, {
        fieldName,
        message,
        contactformName: contactFormSlug,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureContactFormCheckbox = useCallback(
    ({
      fieldName,
      checked,
      required,
      text,
      contactFormSlug,
    }: {
      fieldName: string;
      checked: boolean;
      required: boolean;
      text: string;
      contactFormSlug?: string;
    }) => {
      captureEventWithActiveScreen(PosthogEventName.ContactFormCheckbox, {
        fieldName,
        checked,
        required,
        text,
        contactformName: contactFormSlug,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureContactFormLinkClicked = useCallback(
    (linkUrl: string, linkText: string, contactformName: string) => {
      captureEventWithActiveScreen(PosthogEventName.ContactFormLinkClicked, {
        linkUrl,
        linkText,
        contactformName,
        toInterstitial: shouldShowInterstitial(linkUrl),
      });
    },
    [captureEventWithActiveScreen]
  );

  return {
    captureContactFormOpened,
    captureContactFormView,
    captureContactFormContinue,
    captureContactFormClosed,
    captureContactFormComplete,
    captureContactFormError,
    captureContactFormCheckbox,
    captureContactFormLinkClicked,
  };
}
